import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardBody, Col, Container, CardHeader, Row } from "reactstrap";
import ClientTable from "./ClientTable";
import { useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import ExportCSVModal from "../../Components/Common/ExportCSVModal";
import { useProfile } from "../../Components/Hooks/UserHooks";
import ClientModal from "./ClientModal/ClientModal";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { FilterContainer } from "../../Components/filters/FilterContainer";
import { createSearchFilter } from "../../Components/filters/FilterTypes/SearchFilter";
import { createSelector } from "reselect";
import {
  getClients as onGetClients,
} from "../../slices/thunks";

const selectClientProperties = createSelector(
  (state) => state.Clients,
  (client) => ({
    clients: client.clients || [],
    isClientSuccess: client.isClientSuccess,
    error: client.error,
  })
);

const ClientManagement = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [clientModal, setClientModal] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const { userProfile, isAdmin, isEditor } = useProfile();
  const [clientType, setClientType] = useState(null);

  const { clients, isClientSuccess, error } = useSelector(
    selectClientProperties
  );
  const [filteredData, setFilteredData] = useState([]);

  // Get client type from URL and fetch clients
  useEffect(() => {
    const pathSegments = location.pathname
      .split("/")
      .filter((segment) => segment);
    
    let newClientType;
    let queryParams = {};

    if (pathSegments[0] === "clients" && pathSegments.length > 1) {
      newClientType = pathSegments[1].toUpperCase();
      queryParams = { buyer_type: newClientType };
    } else if (pathSegments[0] === "clients") {
      newClientType = "NATURAL";
      queryParams = { buyer_type: newClientType };
    } else {
      queryParams = Object.fromEntries(new URLSearchParams(location.search));
    }

    // Only update clientType if it changed
    if (newClientType !== clientType) {
      setClientType(newClientType);
    }

    // Always fetch clients when location changes
    dispatch(onGetClients(queryParams));
  }, [dispatch, location.pathname, location.search, clientType]);

  // Filter clients based on type
  const typeFilteredClients = useMemo(() => {
    if (!clients?.length) return [];
    
    return clients.filter((client) => {
      if (clientType === "NATURAL") {
        return client.name && client.surname;
      }
      if (clientType === "LEGAL") {
        return client.legal_company_name;
      }
      return false;
    });
  }, [clients, clientType]);

  const handleDataChange = useCallback((newData) => {
    const processedData = newData.map(item => ({
      ...item, 
      contracts: item.contracts?.map(contract => contract.pvc_number).join(", ") || ""
    }));
  
    setExportData(processedData);
  }, []); 

  // Update filtered data when type filtered clients change
  useEffect(() => {
    if (typeFilteredClients?.length) {
      setFilteredData(typeFilteredClients);
      handleDataChange(typeFilteredClients);
    } else {
      setFilteredData([]);
      handleDataChange([]);
    }
  }, [typeFilteredClients, handleDataChange]);

  // Handle filter changes from FilterContainer
  const handleFilterChange = useCallback(
    (newFilteredData) => {
      setFilteredData(newFilteredData);
      handleDataChange(newFilteredData);
    },
    [handleDataChange]
  );

  // Create search filters based on client type
  const filters = useMemo(() => {
    if (!typeFilteredClients?.length) return [];

    const searchableFields =
      clientType === "NATURAL"
        ? [
            "name",
            "surname",
            "address",
            "mobile_phone_number",
            "personal_email",
          ]
        : [
            "legal_company_name",
            "legal_fiscal_code",
            "business_email",
            "legal_unique_registration_code",
          ];

    return [
      createSearchFilter({
        field: "global",
        label: "Search Clients",
        placeholder: "Search any field...",
        searchableFields,
      }),
    ];
  }, [typeFilteredClients, clientType]);

  return (
    <div className="page-content">
      {/* <BreadCrumb //BreadCrumb problem to wide and makes the page scrollable horizontally 
        title={`List of ${clientType || ""} clients`}
        pageTitle="Clients"
      /> */}
      <Container fluid>
        <div className="mb-4 mx-1">
          <FilterContainer
            filters={filters}
            data={typeFilteredClients}
            onFilterChange={handleFilterChange}
          />
        </div>
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">
                      {clientType === "NATURAL" ? "Natural Persons" : "Legal Entities"}
                    </h5>
                  </div>
                  <div className="col-sm-auto" style={{ marginBottom: "10px" }}>
                    <div className="d-flex gap-1 flex-wrap">
                      {(isAdmin || isEditor) && (
                        <button
                          className="btn btn-success add-btn"
                          onClick={() => setClientModal(true)}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>
                          Create Client
                        </button>
                      )}
                      <button
                        className="btn btn-info"
                        onClick={() => setShowExportModal(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>
                        Export
                      </button>
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                <ClientTable
                  clientType={clientType}
                  filteredData={filteredData}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ClientModal
        show={clientModal}
        clientOption={clientType}
        onCloseClick={() => setClientModal(false)}
      />

      <ExportCSVModal
        show={showExportModal}
        onCloseClick={() => setShowExportModal(false)}
        data={exportData}
      />
    </div>
  );
};

export default ClientManagement;